import './textEditor.scss';

import React, { createContext, useState, useContext, useRef, useEffect } from 'react';
import PropTypes from 'prop-types';
import { Alignment, ButtonGroup, Callout, Intent, Navbar } from '@blueprintjs/core';
import useWindowSize from 'components/hooks/useWindowSize';
import uuid from 'utils/uuid';

import { normalizeTextPlaceholderValue, placeholderTextTransform } from 'editor-utils/template';
import convertColorFromCss from 'editor-utils/polotno/color/convertColorFromCss';

import { Modal } from '../components/rich';
import { Button } from '../components/base';

import useEditorItemContext from './editorItemContext';

import FontFamilySelect from '../polotno/toolbar/text/fontFamilySelect';
import FontSizeSelect from '../polotno/toolbar/text/fontSizeSelect';
import TextAlignSelect from '../polotno/toolbar/text/textAlignSelect';
import TextVerticalAlignSelect from '../polotno/toolbar/text/textVerticalAlignSelect';
import TextColorButton from '../polotno/toolbar/text/textColorButton';

export const verticalAlignment = value => {
  if (value) {
    if (value === 'top') {
      return 'flex-start';
    }
    if (value === 'bottom') {
      return 'flex-end';
    }
    if (value === 'center' || value === 'middle') {
      return 'center';
    }
  }

  // TODO there is an issue...
  // Prawn renders default center vAlign, Polotno renders default top vAlign
  return 'center';
};

const transformOrigin = vAlign => {
  if (vAlign === 'flex-start') {
    return {
      transformOrigin: 'left top',
    };
  }
  if (vAlign === 'flex-end') {
    return {
      transformOrigin: 'left bottom',
      marginTop: 'auto',
    };
  }
  return {
    transformOrigin: 'left center',
    marginTop: 'auto',
    marginBottom: 'auto',
  };
};

export const lineHeight = (placeholder, template) => {
  if (placeholder.layer === 'foil') {
    return 'normal';
  }

  if (template.proxy?.generators?.customGenerator === 'PolotnoGenerator') {
    return 1.15;
  }

  return 'normal';
};

export const textWithHtmlBreaks = text => {
  const parts = text.split('\n').map(value => ({
    value,
    key: uuid(),
  }));

  return (
    <>
      {parts.map((part, idx) => (
        <React.Fragment key={part.key}>
          {part.value}
          {idx < parts.length - 1 && <br />}
        </React.Fragment>
      ))}
    </>
  );
};

const TextEditorContext = createContext();

export const TextEditorContextProvider = props => {
  const { template } = useEditorItemContext();

  const windowSize = useWindowSize();

  const [isVisible, setIsVisible] = useState(props.isVisible);

  const textAreaRef = useRef();

  const [params, setParams] = useState({});

  const [value, setValue] = useState();

  const [textAreaSize, setTextAreaSize] = useState();
  const [fontSize, setFontSize] = useState();
  const [fontName, setFontName] = useState();
  const [fontColor, setFontColor] = useState();
  const [fontAlign, setFontAlign] = useState();
  const [fontVerticalAlign, setFontVerticalAlign] = useState();

  const show = (p = {}) => {
    setIsVisible(true);
    setParams(p);

    const width = (p.placeholder.frame[2] / 2.54) * 72;
    const height = (p.placeholder.frame[3] / 2.54) * 72;

    const scaleX = windowSize.width / (width + 60); // with padding
    const scaleY = windowSize.height / (height + 300); // with nav + padding
    const scale = Math.min(scaleX, scaleY, 2);

    setTextAreaSize({ width, height, scale });

    setValue(p.text || '');
    setFontSize(p.placeholder.fontSize);
    setFontName(p.placeholder.fontName);
    setFontColor(p.placeholder.color || '#000000');
    setFontAlign(p.placeholder.alignment || 'center');
    setFontVerticalAlign(p.placeholder.verticalAlignment || 'middle');
  };

  const hide = () => {
    setIsVisible(false);
    setParams({});
  };

  const focus = () => {
    if (document.activeElement !== textAreaRef.current) {
      textAreaRef.current.focus();
      window.getSelection().selectAllChildren(textAreaRef.current);
      window.getSelection().collapseToEnd();
    }
  };

  useEffect(() => {
    if (isVisible) {
      focus();

      // textAreaRef.current.setSelectionRange(value.length, value.length);
    }
  }, [isVisible]);

  // const handleOnChangeValue = e => {
  //   setValue(
  //     normalizeTextPlaceholderValue(
  //       placeholderTextTransform(e.target.textContent || '', params.placeholder.textTransform),
  //     ),
  //   );
  // };

  const handleOnDoneClick = e => {
    params.onSuccess(
      params.placeholder,
      normalizeTextPlaceholderValue(
        placeholderTextTransform(textAreaRef.current.innerText, params.placeholder.textTransform),
      ),
      {
        fontSize,
        fontName,
        color: fontColor,
        alignment: fontAlign,
        verticalAlignment: fontVerticalAlign,
      },
    );
    hide();
  };

  const getTextAreaFontColor = (hexColor = '') => {
    switch (hexColor.toUpperCase()) {
      case '#FFF':
      case '#FFFFFF':
        return '#000000';
      default:
        return hexColor;
    }
  };

  return (
    <TextEditorContext.Provider
      value={{
        show,
        hide,
      }}
    >
      {props.children}

      {isVisible && (
        <Modal visible size={Math.max(560, textAreaSize.width * textAreaSize.scale + 100)}>
          <Navbar>
            <Navbar.Group align={Alignment.LEFT}>
              <FontFamilySelect
                onChange={val => setFontName(val)}
                options={template.fonts.map(font => font.name)}
                value={fontName}
              />
              <Navbar.Divider />
              <FontSizeSelect
                onChange={val => {
                  setFontSize(val);
                }}
                value={fontSize}
              />
              {params.placeholder.layer !== 'foil' && (
                <>
                  <Navbar.Divider />
                  <ButtonGroup>
                    <TextColorButton
                      onChange={val => setFontColor(convertColorFromCss(val))}
                      value={fontColor}
                    />
                  </ButtonGroup>
                </>
              )}
              <Navbar.Divider />
              <TextAlignSelect onChange={val => setFontAlign(val)} value={fontAlign} />
              <Navbar.Divider />
              <TextVerticalAlignSelect
                onChange={val => setFontVerticalAlign(val)}
                value={fontVerticalAlign}
              />
            </Navbar.Group>
          </Navbar>
          <div className="text-editor-canvas">
            <div
              className="text-editor-wrapper"
              style={{
                width: textAreaSize.width * textAreaSize.scale,
                height: textAreaSize.height * textAreaSize.scale + 50,
              }}
              onClick={e => {
                if (e.target !== textAreaRef.current) {
                  focus();
                }
              }}
            >
              <div
                className="d-flex bg-white"
                style={{
                  overflowY: 'scroll',
                  width: textAreaSize.width * textAreaSize.scale,
                  height: textAreaSize.height * textAreaSize.scale,
                  alignItems: 'flex-start',
                }}
              >
                <span
                  ref={textAreaRef}
                  className="text-editor-area"
                  style={{
                    width: textAreaSize.width,
                    fontSize,
                    color: getTextAreaFontColor(fontColor),
                    fontFamily: fontName,
                    textAlign: fontAlign,
                    transform: `scale(${textAreaSize.scale})`,
                    lineHeight: lineHeight(params.placeholder, template),
                    transition: 'none',
                    ...transformOrigin(verticalAlignment(fontVerticalAlign)),
                  }}
                  contentEditable
                  role="textbox"
                >
                  {textWithHtmlBreaks(value)}
                </span>
              </div>
            </div>
          </div>

          <Callout icon="lightbulb" intent={Intent.WARNING} className="mt-1 text-black">
            Please make sure your text fits in this box, otherwise it will be cut off. Try reducing
            font size or choosing a different font style if it is not fully visible.
          </Callout>

          <Modal.Footer className="mt-1">
            <Button color="bodyText" large onClick={hide}>
              Cancel
            </Button>
            <Button active variant="primary" large onClick={handleOnDoneClick}>
              Done
            </Button>
          </Modal.Footer>
        </Modal>
      )}
    </TextEditorContext.Provider>
  );
};

TextEditorContextProvider.propTypes = {
  isVisible: PropTypes.bool,
  children: PropTypes.node,
};

TextEditorContextProvider.defaultProps = {
  isVisible: false,
};

const useTextEditorContext = () => {
  return useContext(TextEditorContext);
};

export default useTextEditorContext;
